import apiService from "@/core/services/apiService";

export type State = { classes: object };
import {Module, VuexModule, Mutation, Action} from "vuex-module-decorators";
import {Actions, Mutations} from "@/store/enums/store.enums";
import {SubscriptionsActions, SubscriptionsMutation} from "@/modules/subscriptions/store/enums";
import store from "@/store";

@Module
export default class Store extends VuexModule {
    renderKey = 1;
    listParams = {
        page: 1,
        results: 10,
        search: null,
        sort: [],
        filter: {}
    };
    listCache = [];
    listTotal = 0;

    // dropdown caches //
    expertisesCache = [];
    allPackagesCache = [];

    get getSubscriptionsRenderKey() {
        return 'subscriptions-' + this.renderKey;
    }

    get getSubscriptionsLastListResult() {
        return this.listCache;
    }

    get getSubscriptionsLastListTotal() {
        return this.listTotal;
    }

    get getSubscriptionsCourseExpertises() {
        return this.expertisesCache;
    }

    get getSubscriptionsAllPackages() {
        return this.allPackagesCache;
    }

    @Mutation
    [SubscriptionsMutation.SET_RENDER_UPDATE]() {
        this.renderKey++;
    }

    @Mutation
    [SubscriptionsMutation.SET_LIST_RESULT]({listParams, listTotal, listCache}) {
        this.listParams = listParams;
        this.listCache = listCache;
        this.listTotal = listTotal;
    }

    @Action
    [SubscriptionsActions.REFRESH_LIST]() {
        this.context.dispatch(SubscriptionsActions.FETCH_LIST, this.listParams);
    }

    @Action
    [SubscriptionsActions.FETCH_LIST](params) {

        return new Promise<void>((resolve, reject) => {

            apiService.post("subscriptions/list", params).then((response) => {

                const data = response.data || null;

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // return results into storage for reactive update if needed //
                this.context.commit(SubscriptionsMutation.SET_LIST_RESULT, {
                    listParams: params,
                    listTotal: data.results,
                    listCache: data.result
                });
                resolve();

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.GET_ITEM](detailId) {

        return new Promise<void>((resolve, reject) => {

            apiService.get(`subscriptions/get/${detailId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.CREATE_ITEM](payload) {

        return new Promise((resolve, reject) => {

            const {data} = payload;

            apiService.post('subscriptions/create/', data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // flag cached keep alive comp to render new //
                this.context.commit(SubscriptionsMutation.SET_RENDER_UPDATE);

                // refresh list in background//
                this.context.dispatch(SubscriptionsActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.UPDATE_ITEM](payload) {

        return new Promise((resolve, reject) => {

            const {editId, data} = payload;

            apiService.put(`subscriptions/edit/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // refresh list in background//
                this.context.dispatch(SubscriptionsActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.DELETE_ITEM](editId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`subscriptions/delete/${editId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                // refresh list in background//
                this.context.dispatch(SubscriptionsActions.REFRESH_LIST);

                // return results to dispatcher without mutation //
                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // --- Translation --- //

    @Action
    [SubscriptionsActions.UPDATE_TRANSLATION](payload) {

        return new Promise((resolve, reject) => {

            const {editId, data} = payload;

            apiService.post(`subscriptions/update-translation/${editId}`, data).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // --- Members --- //

    @Action
    [SubscriptionsActions.FETCH_MEMBERS](params) {

        return new Promise((resolve, reject) => {

            apiService.post("subscriptions/members", params).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve({ items: data.result, total: data.results });

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.CANCEL_MEMBER](subscriptionId) {

        return new Promise((resolve, reject) => {

            apiService.put(`subscriptions/cancel-member/${subscriptionId}`, {}).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.CLOSE_MEMBER](subscriptionId) {

        return new Promise((resolve, reject) => {

            apiService.put(`subscriptions/close-member/${subscriptionId}`, {}).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.CONFIRM_MEMBER](subscriptionId) {

        return new Promise((resolve, reject) => {

            apiService.put(`subscriptions/confirm-member/${subscriptionId}`, {}).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // --- Package Groups --- //

    @Action
    [SubscriptionsActions.FETCH_PACKAGE_GROUPS](packageId) {

        return new Promise((resolve, reject) => {

            apiService.get(`subscriptions/package-groups/${packageId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.CREATE_PACKAGE_GROUP](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('subscriptions/create-package-group', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.UPDATE_PACKAGE_GROUP](payload) {

        return new Promise((resolve, reject) => {

            const {packageGroupId, data: updateData} = payload;

            apiService.put(`subscriptions/edit-package-group/${packageGroupId}`, updateData).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.DELETE_PACKAGE_GROUP](packageGroupId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`subscriptions/delete-package-group/${packageGroupId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.SORT_PACKAGE_GROUPS](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('subscriptions/sort-package-groups', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // --- Files --- //

    @Action
    [SubscriptionsActions.FETCH_FILES](groupId) {

        return new Promise((resolve, reject) => {

            apiService.get(`subscriptions/files/${groupId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.GET_FILE](fileId) {

        return new Promise((resolve, reject) => {

            apiService.get(`subscriptions/get-file/${fileId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.ADD_FILE](payload) {

        return new Promise((resolve, reject) => {

            apiService.post('subscriptions/add-file', payload).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.UPDATE_FILE](payload) {

        return new Promise((resolve, reject) => {

            const {fileId, data: updateData} = payload;

            // POST statt PUT: Datei-Uploads via FormData — PHP füllt $_FILES nur bei POST //
            apiService.post(`subscriptions/edit-file/${fileId}`, updateData).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.DELETE_FILE](fileId) {

        return new Promise((resolve, reject) => {

            apiService.delete(`subscriptions/delete-file/${fileId}`).then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // --- Dropdowns --- //

    @Action
    [SubscriptionsActions.FETCH_COURSE_EXPERTISES]() {

        return new Promise((resolve, reject) => {

            apiService.get("subscriptions/course-expertises").then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    @Action
    [SubscriptionsActions.FETCH_ALL_PACKAGES]() {

        return new Promise((resolve, reject) => {

            apiService.get("subscriptions/all-packages").then(({data}) => {

                const _verified = apiService.verifyApiResult(data);
                if (!_verified) {
                    this.context.commit(Mutations.SET_ERROR, data);
                    return reject();
                }

                resolve(data.result);

            }).catch((response) => {

                this.context.commit(Mutations.SET_ERROR, response);
                reject();

            });

        });

    };

    // --- Mail + Invoice --- //

    @Action
    [SubscriptionsActions.SEND_MAIL](payload) {
        return new Promise((resolve, reject) => {
            apiService.post("subscriptions/send-mail", payload).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [SubscriptionsActions.CREATE_INVOICE](subscriptionId) {
        return new Promise((resolve, reject) => {
            apiService.post(`subscriptions/create-invoice/${subscriptionId}`, {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [SubscriptionsActions.DOWNLOAD_INVOICE](subscriptionId) {
        return new Promise((resolve, reject) => {
            apiService.query(`subscriptions/download-invoice/${subscriptionId}`, { responseType: 'blob' }).then((response) => {
                // apiService.query rejected NIE — Fehler kommen als resolved value zurück.
                // JSON-Error-Response (z.B. invoiceNotFound) darf nicht als PDF gespeichert werden //
                if (typeof response === "string" || !response.data || (response.data.type && response.data.type.includes("json"))) {
                    this.context.commit(Mutations.SET_ERROR, "invoiceNotFound");
                    return reject();
                }
                const blob = new Blob([response.data], { type: 'application/pdf' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `invoice-${subscriptionId}.pdf`;
                link.click();
                window.URL.revokeObjectURL(url);
                resolve(true);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [SubscriptionsActions.DECLINE_MEMBER](subscriptionId) {
        return new Promise((resolve, reject) => {
            apiService.put(`subscriptions/decline-member/${subscriptionId}`, {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // --- Abos --- //

    @Action
    [SubscriptionsActions.FETCH_ABOS](params) {
        return new Promise((resolve, reject) => {
            apiService.post("subscriptions/abos", params).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                // data.results = Gesamtanzahl (für die Pagination) — wie FETCH_MEMBERS //
                resolve({items: data.result, total: data.results});
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // --- Freigabe-Dashboard --- //

    @Action
    [SubscriptionsActions.FETCH_DASHBOARD]() {
        return new Promise((resolve, reject) => {
            apiService.post("subscriptions/dashboard", {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [SubscriptionsActions.GET_MAIL_TEMPLATE](payload) {
        return new Promise((resolve, reject) => {
            const {mailType, subscriptionId} = payload;
            apiService.get(`subscriptions/get-mail-template/${mailType}/${subscriptionId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // --- Vorschaubild (Upload via FormData 'vo-files[]', Löschen via JSON) --- //

    @Action
    [SubscriptionsActions.UPLOAD_PREVIEW_IMAGE](payload) {
        return new Promise((resolve, reject) => {
            const {groupId, formData} = payload;
            apiService.post(`subscriptions/upload-preview-image/${groupId}`, formData).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    @Action
    [SubscriptionsActions.DELETE_PREVIEW_IMAGE](groupId) {
        return new Promise((resolve, reject) => {
            apiService.post(`subscriptions/delete-preview-image/${groupId}`, {}).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // --- Datei-Inhalt hochladen (2. Stufe nach add-file/edit-file, FormData 'vo-files[]') --- //

    @Action
    [SubscriptionsActions.UPLOAD_FILE_CONTENT](payload) {
        return new Promise((resolve, reject) => {
            const {fileId, formData} = payload;
            apiService.post(`subscriptions/upload-file-content/${fileId}`, formData).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

    // --- Kapitel-Baum in gespeicherter Sortierung --- //

    @Action
    [SubscriptionsActions.FETCH_PACKAGE_GROUPS_SORTED](packageId) {
        return new Promise((resolve, reject) => {
            apiService.get(`subscriptions/package-groups-sorted/${packageId}`).then(({data}) => {
                const _verified = apiService.verifyApiResult(data);
                if (!_verified) { this.context.commit(Mutations.SET_ERROR, data); return reject(); }
                resolve(data.result);
            }).catch((response) => { this.context.commit(Mutations.SET_ERROR, response); reject(); });
        });
    };

}
